import { Col, Row } from 'antd';
import React, { FC } from 'react';
import { ProjectId, UserType } from 'api/models';
import { useProjectVisibilityConfig } from 'components/molecules/project-details/hooks';
import { IntegrationStatusTypes } from 'components/organisms/module-settings/integrations/integrations-list/authentication-types';
import { HideIfNotAllowed } from 'components/organisms/permission-controller';
import { ProjectOverviewV2 } from 'components/organisms/projects/project-overview/right-section/project-details/project-overview-v2/project-details';
import { PermissionRuleName } from 'constant/permission-rules';
import { useProjectDetail } from 'data/projects';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useUser } from 'hooks/use-user';
import {
  useSalesForceAuthenticateQuery,
  useSalesforceConfigQuery,
  useSalesForceGetLinkObjectQuery,
} from 'queries/salesforce-integration';
import {
  ProjectOverviewSalesforceCard,
  ProjectOverviewTeamMembersCard,
  ProjectTimeInfo,
} from './right-section';

export const RightSectionCards: FC<{ projectId: number }> = ({ projectId }) => {
  const { user } = useUser();
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const config = useProjectVisibilityConfig(['project_id', projectId]);

  const { status: authStatus, data: authData } =
    useSalesForceAuthenticateQuery();
  const { status, data } = useSalesforceConfigQuery({
    enabled: authStatus === 'success',
  });

  const { status: projectStatus, data: project } = useProjectDetail(
    ProjectId(projectId),
  );

  const salesforceIntegrationResource = project?.linkedResources?.find(
    (item) => item.externalIntegrationType === 'salesforce',
  );

  const enabled =
    projectStatus === 'success' && salesforceIntegrationResource !== undefined;

  const {
    status: linkedObjectStatus,
    data: linkedObjData,
    refetch,
  } = useSalesForceGetLinkObjectQuery(
    data?.[0]?.integrationId,
    salesforceIntegrationResource?.externalResourceId,
    salesforceIntegrationResource?.externalResourceType,
    {
      enabled,
    },
  );

  return (
    <Row gutter={[16, 16]}>
      <HideIfNotAllowed
        ruleName={PermissionRuleName.ProjectFieldsInOverviewSection}
      >
        <Col span={24}>
          <ProjectOverviewV2 projectId={projectId} />
        </Col>
      </HideIfNotAllowed>
      {status === 'success' &&
        data?.[0] &&
        data?.[0].integrationStatus !== IntegrationStatusTypes.Expired && (
          <Col span={24}>
            <ProjectOverviewSalesforceCard
              projectId={projectId}
              integrationId={data?.[0].integrationId}
              integrationStatus={data?.[0].integrationStatus}
              reauthenticateUrl={authData?.oauthReauthUrl}
              linkedObjData={linkedObjData}
              projectData={salesforceIntegrationResource}
              refetchObj={refetch}
              linkedObjDataStatus={linkedObjectStatus}
              oldIntegrationId={salesforceIntegrationResource?.integrationId}
            />
          </Col>
        )}
      <HideIfNotAllowed ruleName={PermissionRuleName.ProjectHoursSection}>
        {isTimesheetFeatureEnabled &&
          (config?.visibilityMeta?.projectHoursToggle ||
            user.userType === UserType.Native) && (
            <Col span={24}>
              <ProjectTimeInfo projectId={projectId} />
            </Col>
          )}
      </HideIfNotAllowed>
      <Col span={24}>
        <ProjectOverviewTeamMembersCard projectId={projectId} />
      </Col>
    </Row>
  );
};
